import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  addCohort, ApiError, getAdminProfile, getAdminProfileVersion, getAdminRubric,
  getAdminRubricVersion, getPrompt, getRubric, getRubrics, getSettings, putSettings,
  resetSettings, saveAdminProfile, saveAdminRubric, setWriteAllowed, sweepBuildLog,
  syncCourses,
  type Criterion, type HarnessOffer, type ProfileBody, type PromptLayer, type RubricReach,
  type RubricVersionRow, type SweepPreview,
} from "../api";
import { useShell } from "../Layout";
import { GRADING_TABS, isGradingTab, routes, type GradingTab } from "../routes";
import { tracked, useAsync } from "../useAsync";
import {
  Badge, Card, CardBody, CardHead, DASH, ErrorBox, Field, Freshness, Markdown, NumInput, Pane,
  Tier, Trace, When,
} from "../ui";
import { RUN_BOUND } from "../usage";

/** Run the build-log sweep on demand: price it, then run it.
 *
 * ⚠️ TWO CLICKS, DELIBERATELY. The first asks the server what it would cost and how many
 * students it would reach and writes nothing; the second spends. A button that spends a
 * cohort's budget on one click, with no statement of the price, is a control people learn
 * not to trust.
 */
function SweepNow({ courseId, disabled }: { courseId: string; disabled: boolean }) {
  const [days, setDays] = useState<string>("");
  const [pre, setPre] = useState<SweepPreview | null>(null);
  const [msg, setMsg] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const go = (confirm: boolean) => {
    setBusy(true); setMsg("");
    const body: { days?: number; confirm?: boolean } = { confirm };
    const n = parseInt(days, 10);
    if (Number.isFinite(n) && n > 0) body.days = n;
    tracked(sweepBuildLog(courseId, body))
      .then((r) => {
        if (confirm) {
          setPre(null);
          setMsg(r.ran
            ? (r.started ? `Started — collecting ${r.days} days.`
                         : `Nothing to do: ${r.skipped ?? "no students to sweep"}`)
            : `Refused: ${r.reason}`);
        } else { setPre(r); }
      })
      .catch((e) => setMsg((e as ApiError).message || "could not reach the server"))
      .finally(() => setBusy(false));
  };

  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="row gap">
        <button className="btn sm" disabled={disabled || busy} onClick={() => go(false)}>
          {busy ? "Checking…" : "Sweep now"}</button>
        <input className="input" style={{ width: 110 }} value={days} disabled={disabled || busy}
               placeholder="days (opt)" inputMode="numeric"
               onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ""))} />
      </span>
      {pre && (
        <div className="alert">
          <span className="ico">{pre.would_run ? "💸" : "⚠️"}</span>
          <div className="body">
            <div className="t">
              {pre.would_run
                ? `${pre.students} student${pre.students === 1 ? "" : "s"} · ${pre.days} days · about $${(pre.est_usd ?? 0).toFixed(4)}`
                : "This would not run"}
            </div>
            <div className="m">
              {pre.would_run
                ? "Estimated from this cohort's own posting rate at the provider's published rates — it is not a bill, and it errs high. Posts already collected are re-judged, not duplicated."
                : pre.reason}
            </div>
            {pre.would_run && (
              <div className="row gap" style={{ marginTop: 8 }}>
                <button className="btn sm" disabled={busy} onClick={() => go(true)}>
                  {busy ? "Starting…" : "Run it"}</button>
                <button className="btn sm ghost" disabled={busy}
                        onClick={() => setPre(null)}>Cancel</button>
              </div>)}
          </div>
        </div>)}
      {msg && <span className="muted">{msg}</span>}
    </div>);
}

/**
 * How this cohort gets graded: the brain, the limits, the words, and the scale.
 *
 * 🔴 ONE PAGE WITH FOUR TABS, NOT FOUR PAGES. They are four answers to one question, and a
 * setting on one only makes sense beside another — the usage cap is what makes the model
 * choice affordable, and the rubric is what the prompt is asked to apply. A teacher checking
 * "is this cohort set up right?" should not have to visit four URLs and hold four screens in
 * their head.
 *
 * 🔴 ONE DRAFT AND ONE SAVE ACROSS ALL FOUR. Changing the model on Grader and the cap on
 * Behaviour is one edit; the save bar follows you down the page, names the count, and writes
 * CHANGED KEYS ONLY — a full-document PUT would let a stale tab silently revert a key
 * somebody else set.
 */
const LABEL: Record<GradingTab, string> = {
  grader: "Grader", behaviour: "Behaviour", prompt: "Prompt", rubrics: "Rubrics",
};

/** The tab strip, lifted out of `GradingPage` so its one claim can be tested.
 *
 * 🔴 THE READ-ONLY BADGE SAID READ-ONLY TO EVERYBODY, INCLUDING THE ONE PERSON IT IS FALSE
 * FOR. [X] The owner asked why rubrics were read-only; they were signed in as their other
 * address, and this badge agreed with that conclusion from the tab strip — before the panel
 * was even opened. It was the second thing telling them the same wrong story and the only one
 * visible without a click. **A label that states a capability has to read the capability.**
 *
 * ⚠️ IT IS ITS OWN COMPONENT BECAUSE `GradingPage` FETCHES BEFORE IT RENDERS ANYTHING. Testing
 * the badge through the page meant a fixture standing in for the whole settings payload —
 * `engines.harnesses`, `tiers`, `notes` — which is a second copy of the server's
 * shape, drifting from it, for one boolean. Extracting it is cheaper and asserts more.
 */
export function GradingTabs({ tab, href, dirty, isOwner }: {
  tab: GradingTab; href: (t: GradingTab) => string;
  dirty: Set<GradingTab>; isOwner: boolean;
}) {
  return (
    <div className="ptabs" role="tablist">
      {GRADING_TABS.map((x) => (
        <Link key={x} className="ptab" role="tab" aria-selected={tab === x} to={href(x)}>
          {LABEL[x]}
          {dirty.has(x) && <span className="unsaved" title="unsaved changes on this tab" />}
          {x === "rubrics" && !isOwner && <Badge tone="held">read-only</Badge>}
        </Link>))}
    </div>
  );
}

export function GradingPage() {
  const { courseId, access, isOwner } = useShell();
  const { tab: rawTab } = useParams();
  const tab: GradingTab = isGradingTab(rawTab ?? "") ? (rawTab as GradingTab) : "grader";
  // 🔴 WHICH TIER YOU ARE EDITING, defaulting to the global because that is what this page
  // has always written. Changing where somebody's habitual edit lands would be the worst
  // possible way to introduce a second tier; departing for one cohort is a deliberate act.
  const q = useAsync(() => getSettings(courseId), [courseId]);
  // ⚠️ CHANGED KEYS ONLY. A full-document PUT would let a stale tab silently revert a key
  // somebody else set — exactly the lost-update the LearnHouse editor commits.
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState<"" | "course">("");

  if (q.error) return <ErrorBox err={q.error} onRetry={q.reload} />;
  if (!q.data) return <Card><CardBody><div className="skel" /></CardBody></Card>;

  const data = q.data;
  const s = data.settings;
  const val = (k: string) => (k in draft ? draft[k] : s[k]);
  const changed = Object.keys(draft).filter((k) => draft[k] !== s[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });
  const tiers = data.tiers ?? {};
  // ⚠️ PINNING A COHORT TO THE VALUE IT ALREADY INHERITS IS A WRITE — it stops following the
  // global from that moment on, which is exactly what somebody pressing this is asking for.
  // So it sends the effective value at the cohort tier; the server's no-op check is against
  // what is stored THERE rather than against what applies.
  const override = (k: string) => { setScope("course"); setDraft({ ...draft, [k]: val(k) }); };
  const reset = (k: string) => {
    setBusy(true); setErr(null);
    tracked(resetSettings(courseId, [k]))
      .then(() => { setDraft({}); q.reload(); })
      .catch((e) => setErr(e as ApiError))
      .finally(() => setBusy(false));
  };
  const tier = (k: string) => (
    <Tier tier={tiers[k]} editable={data.may_edit && !busy}
          onOverride={() => override(k)} onReset={() => reset(k)} />);
  const overrides = Object.values(tiers).filter((t) => t.source === "course").length;

  const harness = data.engines.harnesses.find((h) => h.id === String(val("engine")))
    ?? data.engines.harnesses[0];
  const models = harness?.models ?? [];
  // 🔴 CHANGING THE HARNESS CAN INVALIDATE THE MODEL. A harness declares which providers it
  // can reach, so not every (harness, model) pair is legal; the pair is checked on the merged
  // document server-side and this picker moves the model with the harness rather than letting
  // them disagree. ⚠️ With one harness shipping this cannot currently fire — kept because the
  // rule is about the relationship, not about how many harnesses happen to exist today.
  const setHarness = (id: string) => {
    const h = data.engines.harnesses.find((x) => x.id === id);
    const keep = h?.models.some((m) => m.id === String(val("model")));
    setDraft({ ...draft, engine: id, ...(keep ? {} : { model: h?.models[0]?.id ?? "" }) });
  };

  const save = () => {
    setBusy(true); setErr(null);
    tracked(putSettings(courseId, Object.fromEntries(changed.map((k) => [k, draft[k]])),
                        scope))
      .then(() => { setDraft({}); q.reload(); })
      .catch((e) => setErr(e as ApiError))
      .finally(() => setBusy(false));
  };

  const dirty = changed.length > 0;
  const href = (x: GradingTab) => routes.grading(courseId, x);

  // Which tab a setting lives on, so the save bar can say where a change was made and a tab
  // with unsaved work can carry a dot. ⚠️ A key absent here still saves; it simply does not
  // light a tab, which is the safe direction.
  const TAB_OF: Record<string, GradingTab> = {
    engine: "grader", model: "grader", effort: "grader",
    auto_grade: "behaviour", auto_grade_max_in_flight: "behaviour",
    auto_grade_weekly_pause_pct: "behaviour", auto_grade_runs_per_day: "behaviour",
    confidence_floor: "behaviour",
    escalate_judged_only: "behaviour", project_form_testers: "behaviour",
  };
  const dirtyTabs = new Set(changed.map((k) => TAB_OF[k] ?? "prompt"));

  return (
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Settings</p>
        <h2>Grading</h2>
        <span className="fresh"><Freshness q={q} /></span>
      </div>

      {/* 🔴 "WHERE IS THIS SET?" ANSWERED BEFORE A TAB IS OPENED. The four tabs answer one
          question between them, and a teacher checking whether the cohort is configured
          correctly should not have to open each one to find out. Every fact links to the tab
          that owns it. */}
      <div className="glance">
        <Link to={href("grader")}><span className="k">Engine</span>
          <span className="v">{String(val("engine") ?? "—")}</span></Link>
        <Link to={href("grader")}><span className="k">Model</span>
          <span className="v">{String(val("model") ?? "—")} · {String(val("effort") ?? "—")}</span></Link>
        <Link to={href("behaviour")}><span className="k">Unattended</span>
          <span className="v">{val("auto_grade")
            ? <Badge tone="warn" dot>on</Badge>
            : <Badge tone="plain">off</Badge>}</span></Link>
        {/* 🔴 USAGE, NOT A PRICE (owner, 2026-10-03): the dollar cap became the
            subscription's weekly figure, with a runs-per-day backstop. Either at 0 means stop. */}
        <Link to={href("behaviour")}><span className="k">Usage cap</span>
          <span className="v">pauses at {Number(val("auto_grade_weekly_pause_pct") ?? 0)}% weekly
            {" "}· {Number(val("auto_grade_runs_per_day") ?? 0)} runs a day
            {(Number(val("auto_grade_weekly_pause_pct") ?? 0) === 0
              || Number(val("auto_grade_runs_per_day") ?? 0) === 0) &&
              <Badge tone="stop" title="0 means stop, never unlimited.">stop</Badge>}</span></Link>
        <Link to={href("behaviour")}><span className="k">Hold for review</span>
          <span className="v">{String(val("escalate_judged_only") ?? "always")}</span></Link>
        <Link to={href("prompt")}><span className="k">Prompt</span>
          <span className="v">nine layers · three editable</span></Link>
        <Link to={href("rubrics")}><span className="k">Rubrics</span>
          <span className="v">generic-v1 is the baseline</span></Link>
      </div>

      {/* 🔴 ABOVE THE TABS, NOT INSIDE ONE. The four tabs share ONE draft and ONE save
          (`routes.ts`: "ONE PAGE, NOT FOUR — a setting on one only makes sense beside
          another"), so a switch per tab would imply four independent scopes over one write.
          ⚠️ RUBRICS HAS NO GLOBAL TIER and the switch says so rather than sitting dead: a
          rubric binds to one assignment in one cohort, so a global default would have
          nothing to bind to. */}
      <div className="scopebar">
        <span className="kick">Editing</span>
        <span className="scopeswitch" role="group" aria-label="Which tier you are editing">
          <button aria-pressed={scope === ""} onClick={() => setScope("")}
                  disabled={tab === "rubrics"}>Global defaults</button>
          <button aria-pressed={scope === "course"} onClick={() => setScope("course")}
                  disabled={tab === "rubrics"}>{access.name}</button>
        </span>
        <span className="spacer" />
        <span className="small muted">
          {tab === "rubrics"
            ? "Rubrics stay per-cohort, with no global tier — a rubric binds to one "
              + "assignment in one cohort, so a global default would have nothing to bind to."
            : overrides === 0
              ? `${access.name} follows every global default on this page, and will move with them.`
              : `${access.name} overrides ${overrides} of ${Object.keys(tiers).length} `
                + "settings. The rest follow the global default and will move with it."}
        </span>
      </div>

      <GradingTabs tab={tab} href={href} dirty={dirtyTabs} isOwner={isOwner} />

      {err && <ErrorBox err={err} />}
      {!data.may_edit && (
        <div className="alert">
          <span className="ico">⚠️</span>
          <div className="body">
            <div className="t">Read-only</div>
            <div className="m">You do not have <code>can_edit_settings</code> on this cohort.
              Everything here is shown and nothing can be saved. ⚠️ A page is hidden only when
              it would be entirely empty, never merely read-only: not being able to see what
              the system is set to is a worse failure than a disabled control.</div>
          </div>
        </div>)}

      {tab === "grader" && (
              <Card>
                <CardHead
                  title="Grader"
                  desc={<>🔴 <strong>An engine is a harness; a model is a model.</strong> The engine
                        is what runs the grading — today there is one, Claude Code in a jail, and it
                        is the only harness whose containment has been measured. The model is the
                        brain. Both are the cohort-wide <em>default</em>: an assignment may override
                        the engine, and the model is never per assignment, because letting it vary
                        would make any comparison between two assignments meaningless.</>}
                  />
                <CardBody>

                  <Field label="Engine" note={harness?.note}
                         hint="The harness that runs the grading. This is the cohort DEFAULT — an assignment can override it on the Assignments tab.">
                    <select className="select" disabled={!data.may_edit}
                            value={String(val("engine"))} onChange={(e) => setHarness(e.target.value)}>
                      {data.engines.harnesses.map((h) =>
                        <option key={h.id} value={h.id}>{h.label}</option>)}
                    </select>
                  </Field>

                  <Field label="Model"
                         hint="Which brain answers, for the whole cohort — never per assignment.
                               Changing it archives nothing, but the fingerprint records it."
                         note={models.find((m) => m.id === String(val("model")))?.note
                               ?? "Which brain answers. Changing it changes what a grade means, and "
                                  + "the fingerprint records that."}>
                    <select className="select" disabled={!data.may_edit}
                            value={String(val("model"))}
                            onChange={(e) => set("model", e.target.value)}>
                      {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                    <ProviderNote h={harness} />
                  </Field>

                  <Field label="Reasoning effort"
                         hint="How hard the model is asked to think. Higher costs more and takes
                               longer; it does not change what the rubric measures."
                         note="Passed to the CLI's --effort flag. It was free text once and reached
                               that flag unchecked; it is an allowlist now.">
                    <select className="select" disabled={!data.may_edit} value={String(val("effort"))}
                            onChange={(e) => set("effort", e.target.value)}>
                      {data.engines.efforts.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </Field>
                </CardBody>
              </Card>
      )}

      {tab === "behaviour" && (
              <Card>
                <CardHead title="Grading behaviour"
                          desc="🔴 A promise implies a mechanism — every setting here is read by
                                something that compares against it." />
                <CardBody>
                  <Field label="Unattended grading" note={data.notes.auto_grade}
                         hint="One of TWO keys — the assignment must also be switched on. A manual Run needs neither.">
                    <select className="select" disabled={!data.may_edit}
                            value={val("auto_grade") ? "on" : "off"}
                            onChange={(e) => set("auto_grade", e.target.value)}>
                      <option value="off">off</option><option value="on">on</option>
                    </select>
                    {tier("auto_grade")}
                    {/* 🔴 THREE TIERS, AND THE THIRD IS AN `and`. A cohort switched on with
                        the assignment unset grades nothing, and until now only the assignment
                        row knew that — this row said "on" and meant "on, if the assignment
                        also says so". `poller.py`'s own comment claimed unset INHERITED the
                        system switch; it never has, and the trace shows what the code does
                        rather than what the comment said. */}
                    <Trace segments={[
                      { tier: "GLOBAL", value: tiers.auto_grade?.inherited ? "on" : "off",
                        state: tiers.auto_grade?.source === "course"
                          ? "superseded" : "wins" },
                      { tier: "COHORT",
                        value: tiers.auto_grade?.source === "course"
                          ? (val("auto_grade") ? "on" : "off") : "—",
                        state: tiers.auto_grade?.source === "course" ? "wins" : "unset" },
                      // ⚠️ NOT A VALUE THIS PAGE HOLDS. It is per assignment, and printing a
                      // guess here would be the same false claim the poller comment made.
                      { tier: "ASSIGNMENT", value: "per assignment", state: "unset" },
                    ]} />
                  </Field>
                  {/* 🔴 USAGE, NOT A PRICE (owner, 2026-10-03). The daily dollar cap became
                      the subscription's own weekly figure, read from the Claude CLI as each run
                      goes, with a runs-per-day backstop for a day it cannot be read. The poller
                      pauses on the first, unattended only; dispatch parks every run on the
                      second, a teacher's included. 0 means stop for both. */}
                  <Field label="Pause unattended grading at weekly usage (%)"
                         note={data.notes.auto_grade_weekly_pause_pct}
                         hint="The subscription’s weekly figure, read from the Claude CLI as each run goes. Once it reaches this, nothing new starts on its own; a teacher’s Run still goes. 0 means stop, never unlimited.">
                    <NumInput mode="numeric" step={1} min={0} max={100} disabled={!data.may_edit}
                              value={Number(val("auto_grade_weekly_pause_pct"))}
                              onCommit={(n) => set("auto_grade_weekly_pause_pct", n)} />
                    {tier("auto_grade_weekly_pause_pct")}
                  </Field>
                  <Field label="Runs per cohort per day" note={data.notes.auto_grade_runs_per_day}
                         hint="Every run counts, a teacher’s included — the backstop for a day the weekly figure cannot be read. 0 means stop, never unlimited.">
                    <NumInput mode="numeric" step={1} min={0} max={100} disabled={!data.may_edit}
                              value={Number(val("auto_grade_runs_per_day"))}
                              onCommit={(n) => set("auto_grade_runs_per_day", n)} />
                    {tier("auto_grade_runs_per_day")}
                  </Field>
                  <Field label="Max grading jobs at once" note={data.notes.auto_grade_max_in_flight}
                         hint="What is still running — the real cap. Raising it queues work rather than finishing it sooner.">
                    <NumInput mode="numeric" min={0} disabled={!data.may_edit}
                              value={Number(val("auto_grade_max_in_flight"))}
                              onCommit={(n) => set("auto_grade_max_in_flight", n)} />
                    {tier("auto_grade_max_in_flight")}
                  </Field>
                  {/* 🔴 A FACT, NOT A SETTING (owner, 2026-10-03: variant B). The per-run dollar
                      ceiling was the CLI's own estimate in API-price dollars — a price nobody
                      pays. The console sends no `--max-budget-usd` now, and the jail stops a run
                      at its turns and its clock, which is what this row states. */}
                  <Field label="Each run stops after"
                         hint="Turns and time already stop a run that loops, and say so in the units a subscription is spent in.">
                    <span className="use-fixed">{RUN_BOUND}</span>
                  </Field>
                  <Field label="Confidence floor" note={data.notes.confidence_floor}
                         hint="A weak control: the model reports its own confidence. A missing one counts as below the floor.">
                    <NumInput step={0.05} min={0} max={1} disabled={!data.may_edit}
                              value={Number(val("confidence_floor"))}
                              onCommit={(n) => set("confidence_floor", n)} />
                    {tier("confidence_floor")}
                  </Field>
                  {/* 🔴 THE ONE SETTING THAT DECIDES WHETHER A MARK CAN REACH A STUDENT WITHOUT A
                      HUMAN. It was reachable only through the API — stored, validated and enforced,
                      with no way to see or change it. */}
                  {/* 🔴 IT GRANTS ONE MARKED ROW, NOT ACCESS. An address here may fill the
                      project form in without being on the Classroom roster; what it writes is
                      `is_test`, which is filtered out of every grouping run by a stored column
                      and never counted among the students who answered. A teacher of the
                      cohort already may, and needs no entry.
                      ⚠️ ONE SCALAR, LIKE EVERY OTHER SETTING. The page decides what is unsaved
                      with `!==`, which is true for two equal arrays every time — a list here
                      would have left a save bar that never clears. */}
                  <Field label="May test the project form"
                         note={data.notes.project_form_testers} wide
                         hint="One address per line. Leave it empty and only teachers of this
                               cohort can test.">
                    <textarea className="ta" rows={3} disabled={!data.may_edit}
                              value={String(val("project_form_testers") ?? "")
                                .split(",").map((x) => x.trim()).filter(Boolean).join("\n")}
                              onChange={(e) => set(
                                "project_form_testers",
                                e.target.value.split(/[\s,;]+/).filter(Boolean).join(", "))} />
                    {tier("project_form_testers")}
                  </Field>
                  <Field label="Hold every grade for review" note={data.notes.escalate_judged_only}
                         hint="Nothing in a score is measured independently, so 'always' is the safe end and the default.">
                    <select className="select" disabled={!data.may_edit}
                            value={String(val("escalate_judged_only") ?? "always")}
                            onChange={(e) => set("escalate_judged_only", e.target.value)}>
                      <option value="always">always — nothing is released unread</option>
                      <option value="on_reason">only for a reason</option>
                    </select>
                    {tier("escalate_judged_only")}
                  </Field>
                </CardBody>
              </Card>
      )}

      {tab === "behaviour" && <CohortCard courseId={courseId} canEdit={data.may_edit} />}

      {tab === "prompt" && (
        <PromptCard courseId={courseId} draft={draft} setDraft={setDraft}
                    changed={changed} busy={busy} save={save} />)}

      {tab === "rubrics" && <RubricsCard courseId={courseId} isOwner={isOwner} />}
      {tab === "rubrics" && <LanguagesCard courseId={courseId} isOwner={isOwner} />}

      {/* One Save for all four tabs, because a change made on one and reviewed on another is
          still one edit. It follows you down the page rather than sitting in a card head that
          scrolls away. */}
      {dirty && data.may_edit && (
        <div className="savebar" role="status">
          <span><b>{changed.length} unsaved change{changed.length === 1 ? "" : "s"}</b>{" "}
            <span className="muted">· on {[...dirtyTabs].map((x) => LABEL[x]).join(", ")}.
              Nothing is applied until you save, and only the changed keys are written.</span>
          </span>
          <span className="spacer" />
          <button className="btn ghost" onClick={() => setDraft({})}>Discard</button>
          <button className="btn primary" disabled={busy} onClick={save}>
            {busy ? "Saving…" : `Save ${changed.length} change${changed.length === 1 ? "" : "s"}`}
          </button>
        </div>)}
      {/* ⚠️ NO "RECENT CHANGES" CARD (owner, 2026-10-04: "they appear on the logs tab"). Who
          changed which setting, and from what, is the Logs page's — scoped to this cohort and
          the console-wide rows. The card here read every cohort's changes. */}
    </div>
  );
}

/**
 * This cohort: whether anything here may reach its students, and which cohorts exist at all.
 *
 * 🔴 READ-ONLY IS THE DEFAULT, AND IT USED TO BE A FROZENSET IN THE IMAGE. Writability is a
 * decision somebody makes here now — with their name on it in the activity log — because the
 * console is meant to serve every cohort a teacher has rather than the two that were compiled
 * into it. What did NOT move is the floor: a cohort on `NEVER_WRITABLE` refuses this control in
 * both directions, and the refusal says so.
 *
 * ⚠️ IT SITS BESIDE "Hold every grade for review" ON PURPOSE. Both answer the same question —
 * what may reach a student — and separating them would put the coarser control somewhere a
 * teacher tuning the finer one never looks.
 */
/** Exported for its own test: the three controls here all change `me`, and the badge one
 *  of them contradicts is in the same `<Field>`. Rendering the whole `GradingPage` to
 *  reach them would need a settings fixture and would test the fixture. */
export function CohortCard({ courseId, canEdit }:
  { courseId: string; canEdit: boolean }) {
  const { access, reloadMe } = useShell();
  const [pre, setPre] = useState<string>("");
  const [want, setWant] = useState<boolean | null>(null);
  const [msg, setMsg] = useState<string>("");
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const go = (allowed: boolean, confirm: boolean) => {
    setBusy(true); setErr(null); setMsg("");
    tracked(setWriteAllowed(courseId, allowed, confirm))
      .then((r) => {
        if (r.preview) { setPre(r.note); setWant(allowed); }
        // 🔴 THE BADGE BESIDE THIS BUTTON READS `access.write_allowed`, WHICH COMES FROM
        // `getMe` AND IS FETCHED ONCE AT BOOT. Without this the control reported success
        // beside a badge still saying the opposite — and the sidebar footer with it.
        // ⚠️ AND THIS ONLY STARTED WORKING ON 2026-09-09. Until then `/me` computed
        // `write_allowed` as `id in WRITE_ALLOWED_COURSES` — the seed frozenset, a constant —
        // so re-reading it returned the same answer however many times it was asked. The
        // client-side half was right and was sitting on top of a server that could not move.
        // Both badges are fed from `config.writable` now, which reads the column.
        else { setPre(""); setWant(null); setMsg(r.note); reloadMe(); }
      })
      .catch((e) => { setPre(""); setErr(e as ApiError); })
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <CardHead title="This cohort"
                desc={<>What may leave this console, and which cohorts it knows about at all.</>} />
      <CardBody>
        {err && <div style={{ marginBottom: 12 }}><ErrorBox err={err} /></div>}

        <Field label="Writing to Classroom"
               hint="Read-only is the default. Grading runs either way and stores marks here;
                     this decides whether a release or an announcement can reach a student."
               note="Turning it on does not release anything. Every write is still one
                     person's decision about one student, still needs `can_release`, and is
                     still recorded in `classroom_writes` — this only decides whether the last
                     mile is open at all. A cohort holding real returned marks is held
                     read-only in code, where a switch cannot reach it.">
          <span className="row gap">
            <span className="badges">
              {access.write_allowed
                ? <Badge tone="ok">writes to Classroom</Badge>
                : <Badge tone="stop">read-only</Badge>}
            </span>
            <button className="btn sm" disabled={!canEdit || busy}
                    onClick={() => go(!access.write_allowed, false)}>
              {busy ? "Checking…"
                    : access.write_allowed ? "Make read-only" : "Allow writing…"}
            </button>
          </span>
        </Field>

        {pre && (
          <div className="alert" role="status" style={{ marginBottom: 12 }}>
            <span className="ico">{want ? "⚠️" : "🔒"}</span>
            <div className="body">
              <div className="t">{want ? "This would open the last mile" : "This would close it"}</div>
              <div className="m">{pre}</div>
              <div className="row gap" style={{ marginTop: 8 }}>
                <button className="btn sm primary" disabled={busy}
                        onClick={() => go(want!, true)}>
                  {want ? "Allow writing" : "Make read-only"}</button>
                <button className="btn sm ghost" disabled={busy}
                        onClick={() => { setPre(""); setWant(null); }}>Cancel</button>
              </div>
            </div>
          </div>)}
        {msg && <p className="alert info" role="status">{msg}</p>}

        <Field label="Add a cohort by id" wide
               hint="For when the credential cannot list courses. Paste the id, or the
                     classroom.google.com/c/… address — the id in that URL is base64 and this
                     accepts either. It arrives read-only."
               note="It is verified before it is recorded, using a scope this console already
                     has: if Classroom will not let it read that cohort's coursework, the id is
                     wrong or the credential's account does not teach it, and adding it here
                     cannot grant that. The name is typed because reading a course's own title
                     needs the listing scope; Find my cohorts corrects it once that exists.">
          <AddCohort disabled={!canEdit} />
        </Field>

        <Field label="Cohorts this console knows about"
               hint="It shows the cohorts recorded here, which is not the same as every cohort
                     you teach. Finding them needs a Classroom scope the credential may not
                     carry — if it does not, the refusal below says which one to grant."
               note="A cohort found this way arrives READ-ONLY and grants you `can_view` and
                     nothing else. Seeing a cohort is not permission to spend money in it, bind
                     a rubric, change its settings or release a mark to anybody.">
          <FindCohorts />
        </Field>
      </CardBody>
    </Card>
  );
}

function AddCohort({ disabled }: { disabled: boolean }) {
  const { reloadMe } = useShell();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="stack tight">
      <span className="row gap">
        <input className="input" style={{ width: 260 }} value={id} disabled={disabled || busy}
               placeholder="id, or the /c/… address" aria-label="Classroom course id"
               onChange={(e) => setId(e.target.value)} />
        <input className="input" style={{ width: 200 }} value={name} disabled={disabled || busy}
               placeholder="what to call it" aria-label="Cohort name"
               onChange={(e) => setName(e.target.value)} />
        <button className="btn sm" disabled={disabled || busy || !id.trim()}
                onClick={() => {
                  setBusy(true); setErr(null); setMsg("");
                  tracked(addCohort(id, name))
                    .then((r) => {
                      setMsg(r.added
                        ? `Added ${r.name} (${r.course_id}), read-only.`
                        : `Already known: ${r.name} (${r.course_id}).`);
                      setId(""); setName("");
                      // The cohort picker is `me.courses`; a cohort added and not listed
                      // reads as an add that silently failed.
                      reloadMe();
                    })
                    .catch((e) => setErr(e as ApiError))
                    .finally(() => setBusy(false));
                }}>{busy ? "Checking…" : "Add"}</button>
      </span>
      {err && <ErrorBox err={err} />}
      {msg && <span className="small muted">{msg}</span>}
    </div>
  );
}

function FindCohorts() {
  const { reloadMe } = useShell();
  const [msg, setMsg] = useState<string>("");
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="stack tight">
      <span>
        <button className="btn sm" disabled={busy} onClick={() => {
          setBusy(true); setErr(null); setMsg("");
          tracked(syncCourses())
            .then((r) => {
              setMsg(`Listed ${r.listed}. Added ${r.added}, renamed ${r.renamed}, ` +
                     `granted you access to ${r.granted}. Every new one is read-only.`);
              reloadMe();
            })
            .catch((e) => setErr(e as ApiError))
            .finally(() => setBusy(false));
        }}>{busy ? "Looking…" : "Find my cohorts"}</button>
      </span>
      {/* 🔴 THE REFUSAL IS THE ANSWER, NOT AN ERROR. "No cohorts found" and "we were not
          allowed to look" are the same empty list, so the sentence that names the scope is
          what a person actually needs. */}
      {err && <ErrorBox err={err} />}
      {msg && <span className="small muted">{msg}</span>}
    </div>
  );
}

/**
 * Social activity: the weekly sweep of what students post publicly.
 *
 * 🔴 ITS OWN PAGE, NOT A GRADING TAB. It spends a different budget on a different thing and
 * reaches no grade — `test_build_log_never_reaches_a_grade` asserts that structurally. Putting
 * it beside the grader's settings would suggest a relationship the schema forbids.
 */
export function SocialPage() {
  const { courseId } = useShell();
  const q = useAsync(() => getSettings(courseId), [courseId]);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  if (q.error) return <ErrorBox err={q.error} onRetry={q.reload} />;
  if (!q.data) return <Card><CardBody><div className="skel" /></CardBody></Card>;

  const data = q.data;
  const s = data.settings;
  const val = (k: string) => (k in draft ? draft[k] : s[k]);
  const changed = Object.keys(draft).filter((k) => draft[k] !== s[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });
  const save = () => {
    setBusy(true); setErr(null);
    tracked(putSettings(courseId, Object.fromEntries(changed.map((k) => [k, draft[k]]))))
      .then(() => { setDraft({}); q.reload(); })
      .catch((e) => setErr(e as ApiError))
      .finally(() => setBusy(false));
  };

  return (
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Settings</p>
        <h2>Social activity</h2>
        <span className="fresh"><Freshness q={q} /></span>
      </div>
      {err && <ErrorBox err={err} />}
            <Card>
              <CardHead title="Social activity"
                        desc={<>What each student has posted publicly, counted against the topics
                              below. 🔴 <strong>Teacher information only</strong> — it reaches no
                              grade, no note to a student and no report. Off-topic posts are
                              counted and never described, because no column exists that could say
                              what one was about.</>} />
              <CardBody>
                <Field label="Weekly check" note={data.notes.build_log_enabled}
                       hint="Off by default. Switching it on starts paying a third party per student, per week.">
                  <select className="select" disabled={!data.may_edit}
                          value={val("build_log_enabled") ? "on" : "off"}
                          onChange={(e) => set("build_log_enabled", e.target.value)}>
                    <option value="off">off</option><option value="on">on</option>
                  </select>
                </Field>
                <Field label="Guidance" note={data.notes.build_log_guidelines} wide
                       hint="The criterion — the only thing that decides on topic. Describe the WORK that counts, not the words. Lands after the safety rules, so it can widen or narrow the judgement and cannot switch one off. Empty means the sweep refuses.">
                  <textarea className="ta" rows={3} disabled={!data.may_edit}
                            value={String(val("build_log_guidelines") ?? "")}
                            onChange={(e) => set("build_log_guidelines", e.target.value)} />
                </Field>
                <Field label="Daily cap (USD, estimated)" note={data.notes.build_log_daily_cap_usd}
                       hint="Bounds the FETCH from twitterapi.io. 0 means stop. Bluesky is free and deliberately not priced; the classifier's own model spend is not counted here.">
                  <NumInput step={0.05} min={0} disabled={!data.may_edit}
                            value={Number(val("build_log_daily_cap_usd"))}
                            onCommit={(n) => set("build_log_daily_cap_usd", n)} />
                </Field>
                <Field label="Every N days" note={data.notes.build_log_interval_days}
                       hint="0 stops the sweep. Each run covers this window plus a fortnight of overlap, so a late run fills its own gap.">
                  <NumInput mode="numeric" min={0} max={90} disabled={!data.may_edit}
                            value={Number(val("build_log_interval_days"))}
                            onCommit={(n) => set("build_log_interval_days", n)} />
                </Field>
              </CardBody>
            </Card>

      <Card>
        <CardHead title="Sweep now"
                  desc="Sweeps immediately instead of waiting for the interval. Every other
                        control still applies — it refuses if the check is off, if the guidance
                        is empty, or if the daily cap is reached, and says which." />
        <CardBody>
          <Field label="Window" wide
                 hint="How many days back to fetch. A wider window re-judges posts already
                       collected, under the current guidance, rather than duplicating them —
                       so this is also how a guidance change reaches work already swept."
                 note="Priced before anything is fetched: the first click asks the server what
                       it would cost and how many students it would reach and writes nothing;
                       the second spends.">
            <SweepNow courseId={courseId} disabled={!data.may_edit} />
          </Field>
        </CardBody>
      </Card>

      {changed.length > 0 && data.may_edit && (
        <div className="savebar" role="status">
          <span><b>{changed.length} unsaved change{changed.length === 1 ? "" : "s"}</b></span>
          <span className="spacer" />
          <button className="btn ghost" onClick={() => setDraft({})}>Discard</button>
          <button className="btn primary" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save"}</button>
        </div>)}
    </div>
  );
}


function ProviderNote({ h }: { h?: HarnessOffer }) {
  if (!h) return null;
  return (
    <p className="field-note" style={{ marginTop: 6 }}>
      {h.providers.includes("anthropic")
        ? "Anthropic-native models run on the Claude credential; gateway models run through "
          + "LiteLLM, where the spend log gives a second witness for attribution."
        : "This engine reaches the gateway's catalogue only — an Anthropic-native model is "
          + "not something it can run, and the pair is refused rather than substituted."}
    </p>
  );
}

// ── the prompt ─────────────────────────────────────────────────────────────────

function PromptCard({ courseId, draft, setDraft, changed, busy, save }: {
  courseId: string; draft: Record<string, unknown>;
  setDraft: (d: Record<string, unknown>) => void;
  changed: string[]; busy: boolean; save: () => void;
}) {
  const q = useAsync(() => getPrompt(courseId), [courseId]);
  const [open, setOpen] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  return (
    <Card>
      <CardHead
        title="The grading prompt"
        desc={<>Every layer the model reads, in order. 🔴 <strong>Everything you can edit sits
              after everything you cannot</strong> — so no instructor text can switch off
              do-not-run-the-code, the citation requirement or the abstention rule.</>}
        right={<>
          <Freshness q={q} />
          <button className="btn sm" onClick={() => setPreview(!preview)}>
            {preview ? "Hide" : "Preview"} assembled</button>
        </>} />
      <CardBody tight>
        <Pane q={q}>{(d) => (<>
          {preview && (() => {
            // 🔴 THE PREVIEW MUST SHOW WHAT YOU ARE ABOUT TO SAVE, NOT WHAT IS SAVED. It was
            // rendered entirely server-side from stored settings, so editing a layer changed
            // nothing here until after a save and a reload — the one moment a preview is for.
            //
            // ⚠️ SPLICED, NOT RE-ASSEMBLED. The server owns the order and the immutable text;
            // this only substitutes the layers being edited into the document it sent. A
            // second assembler in the browser is the divergence that put a different prompt
            // in front of the teacher from the one the model read.
            const edited = d.layers.filter(
              (ly) => ly.setting && ly.setting in draft
                && String(draft[ly.setting]) !== ly.text);
            let body = d.preview.prompt;
            for (const ly of edited) {
              const was = ly.text.trim();
              const now = String(draft[ly.setting]).trim();
              if (was && body.includes(was)) body = body.replace(was, now);
              else if (!was && now) body += `\n\n# ${ly.title}\n\n${now}`;
            }
            const stale = edited.length > 0;
            return (
              <div style={{ padding: 16, borderBottom: "1px solid var(--border)" }}>
                <div className="small muted" style={{ marginBottom: 6 }}>
                  {body.length} characters ·{" "}
                  {stale
                    ? <strong>unsaved edits shown · the fingerprint moves on save</strong>
                    : <>fingerprint <code>{d.sha256.prompt.slice(0, 16)}…</code></>}
                </div>
                <pre className="pre" style={{ maxHeight: 420, overflow: "auto" }}>{body}</pre>
              </div>);
          })()}

          {d.layers.map((ly, i) => (
            <LayerRow key={ly.id} ly={ly} i={i} first={d.first_editable}
                      open={open === ly.id} onToggle={() => setOpen(open === ly.id ? null : ly.id)}
                      mayEdit={d.may_edit}
                      value={ly.setting && ly.setting in draft
                        ? String(draft[ly.setting]) : ly.text}
                      onChange={(v) => ly.setting && setDraft({ ...draft, [ly.setting]: v })} />))}

          {/* ⚠️ FETCHED SINCE IT WAS WRITTEN AND NEVER RENDERED. Its whole purpose is to say
              WHICH layer moved — "a changed fingerprint says two grades are incomparable;
              this says which input moved, which is the difference between a signal and a
              mystery". It was a payload nobody could see. */}
          {d.manifest.length > 0 && (
            <details className="small" style={{ padding: "0 16px 8px" }}>
              <summary>Per-layer fingerprints</summary>
              <table className="mini">
                <thead><tr><th>Layer</th><th>Chars</th><th>Hash</th></tr></thead>
                <tbody>
                  {d.manifest.map((m) => (
                    <tr key={m.id}>
                      <td>{m.title}{m.editable && <span className="muted"> · yours</span>}</td>
                      <td className="num">{m.chars}</td>
                      <td><code>{m.sha256 ? `${m.sha256.slice(0, 12)}…` : "—"}</code></td>
                    </tr>))}
                </tbody>
              </table>
            </details>)}

          <div style={{ padding: "12px 16px" }} className="row">
            <span className="small muted">{d.note}</span>
            <span className="spacer" />
            {changed.length > 0 && (
              <button className="btn primary sm" disabled={busy} onClick={save}>
                {busy ? "Saving…" : "Save"}</button>)}
          </div>
        </>)}</Pane>
      </CardBody>
    </Card>
  );
}

function LayerRow({ ly, i, first, open, onToggle, mayEdit, value, onChange }: {
  ly: PromptLayer; i: number; first: number; open: boolean; onToggle: () => void;
  mayEdit: boolean; value: string; onChange: (v: string) => void;
}) {
  const editable = ly.editable && mayEdit && ly.setting !== "";
  return (
    <div style={{ borderBottom: "1px solid var(--border)",
                  borderTop: i === first ? "2px solid var(--primary)" : undefined }}>
      <div className="asg-row">
        <button className="asg-open" onClick={onToggle}>{open ? "▾" : "▸"}</button>
        <div className="asg-main" onClick={onToggle}>
          <div className="row" style={{ gap: 8 }}>
            <span className="num muted small">{i + 1}</span>
            <strong>{ly.title}</strong>
            {ly.editable
              ? <Badge tone="ok" dot>yours</Badge>
              : <Badge tone="held" title={ly.why}>fixed</Badge>}
            {ly.id === "assignment" &&
              <Badge tone="plain" title="Set per assignment on the Assignments tab.">
                per assignment</Badge>}
          </div>
          <div className="small muted" style={{ marginTop: 3 }}>{ly.why}</div>
        </div>
      </div>
      {open && (
        <div className="asg-panel">
          {editable
            ? <>
                <textarea className="ta" rows={6} value={value}
                          placeholder="Empty — nothing is appended for this layer."
                          onChange={(e) => onChange(e.target.value)} />
                {/* ⚠️ SHOWN THE WAY THE MODEL WILL READ IT. These layers are the only prose a
                    teacher writes that a grading model later consumes, and `Markdown` renders
                    React nodes and never HTML — so there is deliberately no injection surface
                    on either side. Anything it does not cover renders as the literal text
                    that was typed, which is the honest failure. */}
                {value.trim() !== "" && (
                  <details className="small">
                    <summary>Preview</summary>
                    <div className="md sunk" style={{ padding: 10 }}>
                      <Markdown text={value} />
                    </div>
                  </details>)}
              </>
            : ly.editable
              ? <p className="note">{ly.setting
                  ? "You do not have permission to edit this."
                  : "Set on the Assignments tab, per assignment."}</p>
              : <pre className="pre" style={{ maxHeight: 300, overflow: "auto" }}>
                  {ly.text || "(selected by the harness at dispatch)"}</pre>}
        </div>)}
    </div>
  );
}

// ── the rubrics ────────────────────────────────────────────────────────────────

export function RubricsCard({ courseId, isOwner }: { courseId: string; isOwner: boolean }) {
  const q = useAsync(() => getRubrics(courseId), [courseId]);
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card>
      <CardHead
        title="Rubrics"
        desc={<>🔴 <strong>A rubric is what the score is made of.</strong> A criterion marked{" "}
              <code>test</code> is recomputed from the reference suite and the model cannot
              move it; one marked <code>llm</code> is judged, clamped to its weight, and
              scores zero on a citation that does not resolve.</>}
        right={<Freshness q={q} />} />
      <CardBody tight>
        <Pane q={q}>{(d) => (<>
          {d.verified.state === "mismatch" && (
            <div style={{ padding: 16 }}>
              <div className="alert refused" role="alert">
                <span className="ico">⛔</span>
                <div className="body">
                  <div className="t">This console holds a different rubric from the agent</div>
                  <div className="m">{d.verified.mismatched.join("; ")}. Nothing here is safe
                    to grade with until that is resolved.</div>
                </div>
              </div>
            </div>)}
          {d.rubrics.map((r) => (
            <div key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
              <div className="asg-row">
                <button className="asg-open"
                        onClick={() => setOpen(open === r.id ? null : r.id)}>
                  {open === r.id ? "▾" : "▸"}</button>
                <div className="asg-main" onClick={() => setOpen(open === r.id ? null : r.id)}>
                  <div className="row" style={{ gap: 8 }}>
                    <strong><code>{r.id}</code></strong>
                    {r.is_baseline && <Badge tone="plain" title="What every assignment is marked
against, whatever language a submission is in. Suiteless by construction, so every grade under
it escalates.">
                      base</Badge>}
                    {r.retired && <Badge tone="plain" title={r.retired_reason}>retired</Badge>}
                    {r.has_measured_criteria
                      ? <Badge tone="ok" dot>measured</Badge>
                      : <Badge tone="warn" dot title="No reference suite, so nothing is
recomputed and every grade escalates — it can never be released without a human reading it.">
                          judged only</Badge>}
                  </div>
                  <div className="small muted" style={{ marginTop: 3 }}>
                    {r.criteria.length} criteria ·{" "}
                    {r.criteria.filter((c) => c.evidence === "test").length} decided by tests ·{" "}
                    <code>{r.sha256.slice(0, 12)}…</code>
                  </div>
                </div>
              </div>
              {open === r.id && <RubricDetailPanel courseId={courseId} rubricId={r.id}
                                                  isOwner={isOwner} />}
            </div>))}
        </>)}</Pane>
      </CardBody>
    </Card>
  );
}

function RubricDetailPanel({ courseId, rubricId, isOwner }:
  { courseId: string; rubricId: string; isOwner: boolean }) {
  const q = useAsync(() => getRubric(courseId, rubricId), [courseId, rubricId]);
  return (
    <div className="asg-panel">
      <Pane q={q}>{(d) => (
        <div className="stack" style={{ gap: 10 }}>
          <p className="note">{d.cost}</p>
          {/* 🔴 ONE VIEW, NOT TWO — and a reader does not get JSON (owner, 2026-09-25).
              What a grade is made of is the thing every teacher on the cohort has to be able
              to READ; only changing it is estate-wide. A second, simpler component for the
              read-only case would be a second answer to "what does this rubric say", and the
              one nobody edits is the one that drifts. */}
          <RubricEditor rubricId={rubricId} live={d.body} readOnly={!isOwner} />
        </div>)}</Pane>
    </div>
  );
}

/** A rubric body, as much of it as this editor understands.
 *
 * 🔴 UNKNOWN KEYS SURVIVE A SAVE. `gates` is in every shipped rubric and nothing here edits
 * it; a future family will add something else. The draft is the WHOLE parsed object with the
 * edited fields written back over it, never a fresh literal built from the fields below —
 * which is the defect `validate_questions` shipped when it rebuilt each question from a dict
 * and dropped `share` on the first save.
 */
interface Crit {
  id: string; weight: number; evidence: string; label: string; credit_for?: string[];
}
interface Body {
  id?: string; max_points?: number; brief?: string;
  authored?: string[]; ignored?: string[]; criteria?: Crit[];
  testing?: { required?: boolean; what?: string[]; tools?: string[] };
  [k: string]: unknown;
}

type Pick = { kind: "brief" | "files" | "testing" } | { kind: "crit"; i: number };

/** A list of strings, edited in place: add, remove, reorder.
 *
 * 🔴 TWO BUTTONS, NOT A DRAG HANDLE. The mockup drew a grip and a drag target is unreachable
 * from a keyboard, unreadable to a screen reader and untestable in jsdom — and the ORDER of
 * these lines is the order a reader is told to weigh them, so it is content and not decoration.
 * Same decision as the language matrix being radios that look like buttons.
 */
function Lines({ value, onChange, add, mono = false, rows = 2, readOnly = false }: {
  value: string[]; onChange: (v: string[]) => void; add: string;
  mono?: boolean; rows?: number; readOnly?: boolean;
}) {
  const put = (i: number, s: string) =>
    onChange(value.map((v, k) => (k === i ? s : v)));
  const move = (i: number, by: number) => {
    const next = [...value];
    const [x] = next.splice(i, 1);
    next.splice(i + by, 0, x!);
    onChange(next);
  };
  return (<>
    <ul className="rb-lines">
      {value.map((line, i) => (
        <li className="rb-line" key={i}>
          {!readOnly && (
            <span className="rb-move">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)}
                      aria-label={`Move line ${i + 1} up`} title="Move up">▲</button>
              <button type="button" disabled={i === value.length - 1} onClick={() => move(i, 1)}
                      aria-label={`Move line ${i + 1} down`} title="Move down">▼</button>
            </span>)}
          <textarea className={`ta short${mono ? "" : " prose"}`} rows={rows} value={line}
                    aria-label={`Line ${i + 1}`} readOnly={readOnly}
                    onChange={(e) => put(i, e.target.value)} />
          {!readOnly && (
            <button type="button" className="rb-x" title="Remove this line"
                    aria-label={`Remove line ${i + 1}`}
                    onClick={() => onChange(value.filter((_, k) => k !== i))}>×</button>)}
        </li>))}
    </ul>
    {!readOnly && (
      <button type="button" className="rb-add rb-add-inline"
              onClick={() => onChange([...value, ""])}>+ {add}</button>)}
  </>);
}

const strs = (x: unknown): string[] => (Array.isArray(x) ? x.map(String) : []);
const crits = (b: Body): Crit[] => (Array.isArray(b.criteria) ? b.criteria : []);
const total = (b: Body): number =>
  crits(b).reduce((n, c) => n + (Number(c.weight) || 0), 0);

/**
 * Editing one rubric family, owner-only.
 *
 * 🔴 PREVIEW THEN CONFIRM, LIKE THE BINDER — one size up. The binder's blast radius is one
 * assignment; this one is every assignment bound to the family, in every cohort. So the first
 * press asks the server what the edit would reach and writes nothing, and the number it comes
 * back with is on screen before the second press exists.
 *
 * 🔴 SAVING DOES NOT ARCHIVE AND DOES NOT RE-QUEUE, which is a real departure from the binder
 * and is said in those words on the panel. Future runs are marked against the new version,
 * grades already made keep the hash they recorded, and re-grading is a separate, counted act.
 */
function RubricEditor({ rubricId, live, readOnly = false }:
  { rubricId: string; live: unknown; readOnly?: boolean }) {
  const shipped = live as Body;
  const [body, setBody] = useState<Body>(shipped);
  const [at, setAt] = useState<Pick>({ kind: "crit", i: 0 });
  const [note, setNote] = useState("");
  const [pre, setPre] = useState<{ note: string; reach: RubricReach } | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  // ⚠️ THE OWNER'S ROUTE IS NOT ASKED FOR SOMEBODY IT WOULD REFUSE. A panel that
  // fetched the blast radius for a reader who cannot act on it is a 403 rendered as
  // a broken card.
  const meta = useAsync(
    () => (readOnly ? Promise.resolve(null) : getAdminRubric(rubricId)), [rubricId]);

  const edit = (patch: Body) => {
    setBody({ ...body, ...patch }); setPre(null); setMsg("");
  };
  const editCrit = (i: number, patch: Partial<Crit>) =>
    edit({ criteria: crits(body).map((c, k) => (k === i ? { ...c, ...patch } : c)) });

  const dirty = JSON.stringify(body) !== JSON.stringify(shipped);
  const max = Number(body.max_points) || 0;
  const sum = total(body);

  const go = (confirm: boolean) => {
    setBusy(true); setMsg("");
    tracked(saveAdminRubric(rubricId, { body, note, confirm }))
      .then((r) => {
        if (r.saved) {
          setPre(null); setNote("");
          setMsg(`Saved as version ${r.version}. Runs from now on are marked against it; `
                 + "nothing was archived and nothing was re-queued.");
          meta.reload();
        } else { setPre({ note: r.note ?? "", reach: r.reach }); }
      })
      .catch((e) => {
        setPre(null); setMsg((e as ApiError).message || "could not reach the server");
      })
      .finally(() => setBusy(false));
  };

  const cs = crits(body);
  const here = at.kind === "crit" ? cs[Math.min(at.i, cs.length - 1)] : undefined;
  const hasGlobs = "authored" in shipped || "ignored" in shipped;

  return (
    <div className="stack" style={{ gap: 0 }}>
      {meta.data && (
        <div style={{ padding: "0 0 12px" }}><RubricReachStrip reach={meta.data.reach} /></div>)}

      <div className="rb-body">
        <nav className="rb-outline" aria-label="Rubric sections">
          <div className="rb-group">What the mark is made of</div>
          <button type="button" className={`rb-item${at.kind === "brief" ? " on" : ""}`}
                  onClick={() => setAt({ kind: "brief" })}>
            <span className="rb-item-text">
              <span className="rb-item-t">Brief</span>
              <span className="rb-item-m">what this rubric is for</span>
            </span>
          </button>

          <div className="rb-group rb-group-row">
            <span>Criteria</span>
            {/* 🔴 THE ONE RULE THAT PARKS A WHOLE COHORT WHEN IT IS WRONG, so it is on screen
                permanently rather than discovered at save. `validate_rubric` refuses a body
                whose weights do not sum to the maximum, and before this the only way to learn
                that was to press Save. */}
            <span className={`rb-sum ${sum === max ? "ok" : "bad"}`}
                  title="Every criterion's weight must add up to the maximum">
              {cs.map((c) => c.weight).join(" + ")} = {sum}{sum === max ? " ✓" : ` of ${max}`}
            </span>
          </div>
          {cs.map((c, i) => (
            <button type="button" key={c.id || i}
                    className={`rb-item${at.kind === "crit" && at.i === i ? " on" : ""}`}
                    onClick={() => setAt({ kind: "crit", i })}>
              <span className="rb-item-text">
                <span className="rb-item-t"><code>{c.id}</code> {c.label || "(unnamed)"}</span>
                <span className="rb-item-m">
                  {strs(c.credit_for).length} credit line
                  {strs(c.credit_for).length === 1 ? "" : "s"} ·{" "}
                  {c.evidence === "test" ? "measured by tests" : "judged by reading"}
                </span>
              </span>
              <span className="rb-w">{c.weight}</span>
            </button>))}
          <button type="button" className="rb-add" disabled={readOnly}
                  onClick={() => {
                    // 🔴 FROM THE HIGHEST IN THE SAVED BODY **AND** THE DRAFT, not from the
                    // count and not from the draft alone. Every stored grade, every binding
                    // override and every per-criterion review names a criterion by its id, so
                    // reusing a retired one makes an old C2 and a new C2 two different things
                    // with one name. [X] Counting and taking the draft's own max agree on a
                    // rubric nobody has edited, which is why the first test could not tell
                    // them apart and the mutation came back SILENT — the case that separates
                    // them is removing one and adding another.
                    // ⚠️ THE LIMIT, STATED: an id retired in an EARLIER session and saved is
                    // not in either list, so it can still come back. Closing that needs the
                    // highest id across every stored version, which is the server's to know.
                    const num = (c: Crit) => Number(/^C(\d+)$/.exec(c.id || "")?.[1] ?? 0);
                    const n = [...crits(shipped), ...cs]
                      .reduce((m, c) => Math.max(m, num(c)), 0) + 1;
                    edit({ criteria: [...cs, { id: `C${n}`, weight: 0, evidence: "llm",
                                               label: "", credit_for: [] }] });
                    setAt({ kind: "crit", i: cs.length });
                  }}>+ Add a criterion</button>

          <div className="rb-group">Everything else</div>
          {/* ⚠️ ONLY WHILE THE BODY HAS GLOBS. Since 2026-09-27 which files are the student's
              own is the LANGUAGE's fact — each profile carries them — and the base has none;
              a pane editing keys the document does not have would add them back. */}
          {hasGlobs && (
          <button type="button" className={`rb-item${at.kind === "files" ? " on" : ""}`}
                  onClick={() => setAt({ kind: "files" })}>
            <span className="rb-item-text">
              <span className="rb-item-t">The student's own files</span>
              <span className="rb-item-m">
                {strs(body.authored).length} counted · {strs(body.ignored).length} ignored
              </span>
            </span>
          </button>)}
          <button type="button" className={`rb-item${at.kind === "testing" ? " on" : ""}`}
                  onClick={() => setAt({ kind: "testing" })}>
            <span className="rb-item-text">
              <span className="rb-item-t">Testing</span>
              <span className="rb-item-m">
                {body.testing?.required ? "required" : "not required"} ·{" "}
                {strs(body.testing?.what).length} expectation
                {strs(body.testing?.what).length === 1 ? "" : "s"}
              </span>
            </span>
          </button>
        </nav>

        <section className="rb-editor"
                 aria-label={at.kind === "crit" ? `Editing ${here?.id ?? ""}` : `Editing ${at.kind}`}>
          {at.kind === "brief" && (
            <div className="rb-row" style={{ borderBottom: 0 }}>
              <div className="rb-label rb-label-block">Brief</div>
              <div className="rb-ctl">
                <p className="rb-hint">What this rubric is for, in the model's words. It is read
                  before any criterion and says what kind of work this family marks — not what
                  any one assignment asked for, which is the assignment's own brief.</p>
                <textarea className="ta prose" rows={8} value={String(body.brief ?? "")}
                          aria-label="Brief" readOnly={readOnly}
                          onChange={(e) => edit({ brief: e.target.value })} />
              </div>
            </div>)}

          {at.kind === "crit" && here && (<>
            <header className="rb-ed-head">
              <div>
                <div className="rb-ed-eyebrow">Criterion <code>{here.id}</code></div>
                <input className="rb-ed-title" value={here.label ?? ""} readOnly={readOnly}
                       aria-label="What this criterion is called"
                       placeholder="What this criterion is called"
                       onChange={(e) => editCrit(at.i, { label: e.target.value })} />
              </div>
              {/* ⚠️ REMOVING ONE BREAKS THE WEIGHTS UNTIL ANOTHER IS RAISED, and the sum in
                  the outline says so the moment it happens. It is not disabled: the teacher
                  may be mid-way through replacing a criterion, and a control that refuses the
                  first half of a two-step edit is a control you cannot use. */}
              {!readOnly && (
              <button type="button" className="btn sm ghost rb-del"
                      onClick={() => {
                        edit({ criteria: cs.filter((_, k) => k !== at.i) });
                        setAt({ kind: "crit", i: Math.max(0, at.i - 1) });
                      }}>Remove</button>)}
            </header>

            <div className="rb-row">
              <label className="rb-label" htmlFor="rb-w">Worth</label>
              <div className="rb-ctl rb-weight">
                <input className="input" id="rb-w" inputMode="numeric" readOnly={readOnly}
                       value={String(here.weight ?? 0)}
                       onChange={(e) => editCrit(at.i, {
                         weight: Number(e.target.value.replace(/[^0-9]/g, "")) || 0 })} />
                <span className="rb-unit">of {max}</span>
                <span />
                <div className="rb-bar" role="img"
                     aria-label={`${here.id} is ${here.weight} of ${max} points`}>
                  <span className="rb-bar-fill"
                        style={{ width: `${max ? (Number(here.weight) / max) * 100 : 0}%` }} />
                  <span className="rb-bar-rest" style={{ flex: 1 }} />
                </div>
              </div>
            </div>

            <div className="rb-row">
              <label className="rb-label" htmlFor="rb-ev">Decided by</label>
              <div className="rb-ctl">
                {/* 🔴 THE SECOND OPTION IS DISABLED WITH THE REASON ON THE PAGE, not in a
                    tooltip — you cannot hover an option you cannot open. Same shape as
                    `UNAVAILABLE_STACKS`: making a thing selectable and making it work are two
                    changes, and `validate_rubric` refuses `test` before anything is spent. */}
                <select className="select" id="rb-ev" value={here.evidence ?? "llm"}
                        disabled={readOnly}
                        onChange={(e) => editCrit(at.i, { evidence: e.target.value })}>
                  <option value="llm">a model reading the code</option>
                  <option value="test" disabled>
                    running a reference suite — none ships with this rubric</option>
                </select>
                <p className="rb-hint">A judged criterion is clamped to its weight and scores
                  zero when none of its citations resolve. Measuring needs a test pack in the
                  build image, which no family has yet — so every grade under this rubric
                  escalates and cannot be released without a person reading it.</p>
              </div>
            </div>

            <div className="rb-row" style={{ borderBottom: 0 }}>
              <div className="rb-label rb-label-block">
                What earns the marks
                <span className="rb-count">{strs(here.credit_for).length} lines</span>
              </div>
              <div className="rb-ctl">
                <p className="rb-hint">One statement per line, in the order a reader should
                  weigh them. These are the sentences the model is told to credit — and the
                  ones a student sees quoted back when it takes marks off.</p>
                <Lines value={strs(here.credit_for)} add="Add a line"
                       onChange={(v) => editCrit(at.i, { credit_for: v })} readOnly={readOnly} />
              </div>
            </div>
          </>)}

          {at.kind === "files" && hasGlobs && (<>
            <div className="rb-row">
              <div className="rb-label rb-label-block">
                Counted as theirs
                <span className="rb-count">{strs(body.authored).length} patterns</span>
              </div>
              <div className="rb-ctl">
                <p className="rb-hint">Globs, relative to the submission root. A file matching
                  none of these reads as <code>other</code> in the authorship report — which is
                  the absence of a judgement, not a judgement. <strong>No patterns at all means
                  no classification</strong>, deliberately: the rubric decides what the student
                  wrote, and a harness guessing a layout once told a whole cohort none of their
                  files were their own.</p>
                <Lines value={strs(body.authored)} add="Add a pattern" mono rows={1}
                       onChange={(v) => edit({ authored: v })} readOnly={readOnly} />
              </div>
            </div>
            <div className="rb-row" style={{ borderBottom: 0 }}>
              <div className="rb-label rb-label-block">
                Ignored
                <span className="rb-count">{strs(body.ignored).length} patterns</span>
              </div>
              <div className="rb-ctl">
                <p className="rb-hint">Build output, lockfiles and vendored code — anything the
                  student did not write and should be neither credited nor blamed for.</p>
                <Lines value={strs(body.ignored)} add="Add a pattern" mono rows={1}
                       onChange={(v) => edit({ ignored: v })} readOnly={readOnly} />
              </div>
            </div>
          </>)}

          {at.kind === "testing" && (<>
            <div className="rb-row">
              <label className="rb-label" htmlFor="rb-req">Tests are</label>
              <div className="rb-ctl">
                <select className="select" id="rb-req" disabled={readOnly}
                        value={body.testing?.required ? "yes" : "no"}
                        onChange={(e) => edit({
                          testing: { ...body.testing, required: e.target.value === "yes" } })}>
                  <option value="no">not required — their absence is not itself a fault</option>
                  <option value="yes">required — a project with none loses marks</option>
                </select>
                {/* ⚠️ THE FLAG DECIDES ONLY WHETHER ABSENCE IS A FAULT. What good testing looks
                    like lives in the expectations below, and is read either way — which is why
                    `typescript-v1` is `not required` and still says when tests ARE expected. */}
                <p className="rb-hint">This decides one thing: whether having no tests is a
                  fault by itself — and only for an assignment that has not said. Each
                  assignment can set its own under <strong>Their own tests</strong> on the
                  Assignments page. What counts as testing something properly is the list below,
                  and the model reads it whichever way this is set.</p>
              </div>
            </div>
            <div className="rb-row">
              <div className="rb-label rb-label-block">
                What should be tested
                <span className="rb-count">{strs(body.testing?.what).length} lines</span>
              </div>
              <div className="rb-ctl">
                <Lines value={strs(body.testing?.what)} add="Add an expectation"
                       onChange={(v) => edit({ testing: { ...body.testing, what: v } })}
                       readOnly={readOnly} />
              </div>
            </div>
            <div className="rb-row" style={{ borderBottom: 0 }}>
              <div className="rb-label rb-label-block">
                What to write them with
                <span className="rb-count">{strs(body.testing?.tools).length} suggestions</span>
              </div>
              <div className="rb-ctl">
                <p className="rb-hint">What a <em>student</em> may reasonably use — not what
                  the build image carries. The agent never installs a test framework into a
                  submission: a missing toolchain is our problem, never a mark against them.</p>
                <Lines value={strs(body.testing?.tools)} add="Add a tool" mono rows={1}
                       onChange={(v) => edit({ testing: { ...body.testing, tools: v } })}
                       readOnly={readOnly} />
              </div>
            </div>
          </>)}
        </section>
      </div>

      {readOnly
        ? <p className="small muted" style={{ marginTop: 12 }}>
            Editing a rubric family changes every assignment bound to it, in every cohort, at
            once — so it is not a cohort permission, and this panel is read-only for everybody
            but whoever administers this console.
          </p>
        : <>
      <div className="rb-save">
        <span className="rb-save-m">
          {dirty
            ? <>Unsaved changes. <strong>Nothing is written</strong> until you check and
                confirm.</>
            : "No changes yet."}
        </span>
        <span className="row gap">
          {dirty && (
            <button className="btn sm ghost" disabled={busy}
                    onClick={() => { setBody(shipped); setPre(null); setMsg(""); }}>
              Discard my edits</button>)}
          <button className="btn sm" disabled={busy || !dirty} onClick={() => go(false)}>
            {busy ? "Checking…" : "Check what this reaches"}</button>
        </span>
      </div>

      {/* 🔴 THE CONFIRM SITS INSIDE THE STATEMENT IT CONSENTS TO, which is the shape every
          other preview-then-confirm control in this console already has. [X] Seen in a render
          with it in the button row: pressing Check put the sentence about 412 stored grades
          BELOW the button that acts on it, so the reader had to look down to read what they
          were agreeing to and back up to agree. */}
      {pre && (
        <div className="alert" role="status" style={{ marginTop: 12 }}>
          <span className="ico">⚠️</span>
          <div className="body">
            <div className="t">Nothing has been written yet</div>
            <div className="m">{pre.note}</div>
            <Field wide label="Why"
                   hint="Kept with the version, so a later reader knows what this changed and
for which programme. It reaches no student and no model.">
              <textarea className="ta short prose" rows={2} value={note} maxLength={2000}
                        placeholder="optional — what this edit is for"
                        onChange={(e) => setNote(e.target.value)} />
            </Field>
            <div className="row gap" style={{ marginTop: 8 }}>
              <button className="btn sm primary" disabled={busy} onClick={() => go(true)}>
                Save version {(meta.data?.reach.current_version ?? 0) + 1}</button>
              <button className="btn sm ghost" disabled={busy}
                      onClick={() => setPre(null)}>Cancel</button>
            </div>
          </div>
        </div>)}
      {msg !== "" && (
        <div className={`alert${msg.startsWith("Refused") || msg.startsWith("Infrastructure")
                                ? " refused" : ""}`} role="status" style={{ marginTop: 12 }}>
          <span className="ico">{msg.startsWith("Saved") ? "✅" : "⛔"}</span>
          <div className="body"><div className="m">{msg}</div></div>
        </div>)}
      <p className="small muted" style={{ marginTop: 10 }}>
        🔴 <strong>A rubric family is not a property of a cohort.</strong> Saving this changes
        what <em>future</em> runs on every bound assignment are marked against. Grades already
        stored keep the rubric they were marked against and are <strong>not</strong> archived
        and <strong>not</strong> re-queued — re-grading is a separate act with its own cost.
      </p>
      {meta.data && <RubricVersions rubricId={rubricId} rows={meta.data.versions}
                                    onLoad={(b) => {
                                      setBody(b as Body); setPre(null); setMsg("");
                                    }} />}
      {meta.error && <ErrorBox err={meta.error} onRetry={meta.reload} />}
        </>}
    </div>
  );
}

/** What an edit reaches, stated before it is taken. */
function RubricReachStrip({ reach }: { reach: RubricReach }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row gap small">
        <Badge tone="plain">version {reach.current_version}</Badge>
        {/* ⚠️ THE SOURCE IS THE FACT THAT STOPS A DEPLOY BEING A SURPRISE. `file` means the
            next deploy still ships this rubric's body; `console` means it does not, because
            somebody edited it here. */}
        <Badge tone={reach.current_source === "console" ? "warn" : "plain"}>
          {reach.current_source === "console" ? "edited here" : "as shipped"}</Badge>
        <span className="muted">
          {reach.assignments} assignment{reach.assignments === 1 ? "" : "s"} ·{" "}
          {reach.cohorts.length} cohort{reach.cohorts.length === 1 ? "" : "s"} ·{" "}
          {reach.graded_under_current} stored grade
          {reach.graded_under_current === 1 ? "" : "s"}
        </span>
      </div>
      {reach.cohorts.length > 0 && (
        <div className="row gap small wrap">
          {reach.cohorts.map((c) => <Badge key={c.id} tone="plain">{c.name}</Badge>)}
        </div>)}
      {reach.differs_from_shipped && (
        <div className="alert" role="status">
          <span className="ico">⚠️</span>
          <div className="body">
            <div className="t">The shipped file has moved underneath this edit</div>
            <div className="m">A later deploy changed the vendored copy of this rubric and the
              version edited here is what grading uses — deliberately, so an edit does not
              revert when nobody is looking. Nothing is broken; the two have diverged, and only
              somebody reading both can decide which is wanted.</div>
          </div>
        </div>)}
    </div>
  );
}

/** Every body this rubric has had. */
function RubricVersions({ rubricId, rows, onLoad, fetchVersion = getAdminRubricVersion,
                          countLabel = "criteria" }:
  { rubricId: string; rows: RubricVersionRow[]; onLoad: (body: unknown) => void;
    fetchVersion?: (id: string, v: number) => Promise<{ body: unknown }>;
    countLabel?: string }) {
  const [busy, setBusy] = useState(0);
  const [err, setErr] = useState("");
  const load = (v: number) => {
    setBusy(v); setErr("");
    tracked(fetchVersion(rubricId, v))
      .then((r) => onLoad(r.body))
      .catch((e) => setErr((e as ApiError).message || "could not reach the server"))
      .finally(() => setBusy(0));
  };
  return (
    <details className="small">
      <summary>History — {rows.length} version{rows.length === 1 ? "" : "s"}</summary>
      {err !== "" && <p className="note">{err}</p>}
      <table className="tb mini">
        <thead><tr>
          <th>v</th><th>from</th><th>by</th><th>when</th><th>{countLabel}</th><th>why</th>
          <th />
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.version}>
              <td>{r.version}</td>
              <td>{r.source === "console" ? "edited here" : "shipped"}</td>
              <td>{r.created_by || DASH}</td>
              <td><When iso={r.created_at} /></td>
              <td>{r.criteria}</td>
              <td className="wrap">{r.note || DASH}</td>
              <td>
                {/* ⚠️ LOAD, NOT REVERT. It fills the editor; saving it makes a NEW version,
                    because going back is itself an act with an actor and a time and not a
                    deletion of what happened in between. */}
                <button className="btn sm ghost" disabled={busy !== 0}
                        onClick={() => load(r.version)}>
                  {busy === r.version ? "…" : "Load"}</button>
              </td>
            </tr>))}
        </tbody>
      </table>
    </details>
  );
}



/** The language profiles: how craft reads in each language, merged into the base per submission.
 *
 * 🔴 THE CRITERIA ARE THE SAME FOR EVERY LANGUAGE (owner, 2026-09-27); THESE ARE WHAT IS NOT.
 * A student may answer in a different language from the next, so the language is
 * detected per submission from its files, and the matching profile's lines are added to the
 * base criteria before the job starts. Estate-wide, like a rubric family: owner-only to edit,
 * readable by every teacher, versioned, and saving archives nothing.
 */
export function LanguagesCard({ courseId, isOwner }: { courseId: string; isOwner: boolean }) {
  const q = useAsync(() => getRubrics(courseId), [courseId]);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card>
      <CardHead
        title="Languages"
        desc={<>Every submission is marked against the same criteria. The console decides which
              language each one is written in from its files, and adds that language's lines
              below to the criteria — <strong>added to them, never instead of them</strong>.</>}
        right={<Freshness q={q} />} />
      <CardBody tight>
        <Pane q={q}>{(d) => {
          const base = d.rubrics.find((r) => r.id === d.default_rubric_id);
          return (<>
            {(d.profiles ?? []).map((p) => (
              <div key={p.id} style={{ borderBottom: "1px solid var(--border)" }}>
                <div className="asg-row">
                  <button className="asg-open" aria-label={`Open ${p.label}`}
                          onClick={() => setOpen(open === p.id ? null : p.id)}>
                    {open === p.id ? "▾" : "▸"}</button>
                  <div className="asg-main" onClick={() => setOpen(open === p.id ? null : p.id)}>
                    <div className="row" style={{ gap: 8 }}>
                      <strong>{p.label}</strong>
                      <Badge tone="plain">version {p.version}</Badge>
                    </div>
                    <div className="small muted" style={{ marginTop: 3 }}>
                      {Object.values(p.body.craft ?? {}).flat().length} craft line
                      {Object.values(p.body.craft ?? {}).flat().length === 1 ? "" : "s"} ·{" "}
                      {(p.body.authored ?? []).length} file pattern
                      {(p.body.authored ?? []).length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                {open === p.id && (
                  <div className="asg-panel">
                    <ProfileEditor profileId={p.id} live={p.body}
                                   baseCriteria={base?.criteria ?? []} readOnly={!isOwner} />
                  </div>)}
              </div>))}
          </>);
        }}</Pane>
      </CardBody>
    </Card>
  );
}

type PPick = { kind: "about" | "files" | "testing" } | { kind: "craft"; id: string };

/** One language profile, edited with the rubric editor's own parts.
 *
 * 🔴 THE BASE'S LINES ARE SHOWN BESIDE THE CRAFT LINES, READ-ONLY. A craft line means nothing
 * alone — it is how one criterion reads in one language — so the reader sees what it is being
 * added to while writing it.
 * ⚠️ UNKNOWN KEYS SURVIVE A SAVE, the RubricEditor rule: the draft is the parsed body with
 * edited fields written over it, never a fresh literal.
 */
function ProfileEditor({ profileId, live, baseCriteria, readOnly = false }: {
  profileId: string; live: ProfileBody; baseCriteria: Criterion[]; readOnly?: boolean;
}) {
  const [body, setBody] = useState<ProfileBody>(live);
  const [at, setAt] = useState<PPick>({ kind: "about" });
  const [note, setNote] = useState("");
  const [pre, setPre] = useState<{ note: string; reach: RubricReach } | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  // ⚠️ THE OWNER'S ROUTE IS NOT ASKED FOR SOMEBODY IT WOULD REFUSE — the RubricEditor rule.
  const meta = useAsync(
    () => (readOnly ? Promise.resolve(null) : getAdminProfile(profileId)), [profileId]);

  const edit = (patch: Partial<ProfileBody>) => {
    setBody({ ...body, ...patch }); setPre(null); setMsg("");
  };
  const dirty = JSON.stringify(body) !== JSON.stringify(live);
  const craft = body.craft ?? {};

  const go = (confirm: boolean) => {
    setBusy(true); setMsg("");
    tracked(saveAdminProfile(profileId, { body, note, confirm }))
      .then((r) => {
        if (r.saved) {
          setPre(null); setNote("");
          setMsg(`Saved as version ${r.version}. Runs from now on read ${body.label} this way; `
                 + "nothing was archived and nothing was re-queued.");
          meta.reload();
        } else { setPre({ note: r.note ?? "", reach: r.reach }); }
      })
      .catch((e) => {
        setPre(null); setMsg((e as ApiError).message || "could not reach the server");
      })
      .finally(() => setBusy(false));
  };

  const here = at.kind === "craft" ? baseCriteria.find((c) => c.id === at.id) : undefined;
  return (
    <div className="stack" style={{ gap: 0 }}>
      {meta.data && (
        <div style={{ padding: "0 0 12px" }}><RubricReachStrip reach={meta.data.reach} /></div>)}
      <div className="rb-body">
        <nav className="rb-outline" aria-label={`${live.label} sections`}>
          <div className="rb-group">What it is</div>
          <button type="button" className={`rb-item${at.kind === "about" ? " on" : ""}`}
                  onClick={() => setAt({ kind: "about" })}>
            <span className="rb-item-text">
              <span className="rb-item-t">Name and brief</span>
              <span className="rb-item-m">what a submission in this language is</span>
            </span>
          </button>
          <div className="rb-group">Craft, per criterion</div>
          {baseCriteria.map((c) => (
            <button type="button" key={c.id}
                    className={`rb-item${at.kind === "craft" && at.id === c.id ? " on" : ""}`}
                    onClick={() => setAt({ kind: "craft", id: c.id })}>
              <span className="rb-item-text">
                <span className="rb-item-t"><code>{c.id}</code> {c.label || "(unnamed)"}</span>
                <span className="rb-item-m">
                  {(craft[c.id] ?? []).length} line{(craft[c.id] ?? []).length === 1 ? "" : "s"}
                  {" "}added</span>
              </span>
            </button>))}
          <div className="rb-group">Everything else</div>
          <button type="button" className={`rb-item${at.kind === "files" ? " on" : ""}`}
                  onClick={() => setAt({ kind: "files" })}>
            <span className="rb-item-text">
              <span className="rb-item-t">The student's own files</span>
              <span className="rb-item-m">
                {strs(body.authored).length} counted · {strs(body.ignored).length} ignored
              </span>
            </span>
          </button>
          <button type="button" className={`rb-item${at.kind === "testing" ? " on" : ""}`}
                  onClick={() => setAt({ kind: "testing" })}>
            <span className="rb-item-text">
              <span className="rb-item-t">Testing</span>
              <span className="rb-item-m">
                {strs(body.testing?.what).length} expectation
                {strs(body.testing?.what).length === 1 ? "" : "s"} ·{" "}
                {strs(body.testing?.tools).length} tool
                {strs(body.testing?.tools).length === 1 ? "" : "s"}
              </span>
            </span>
          </button>
        </nav>

        <section className="rb-editor" aria-label={`Editing ${live.label}`}>
          {at.kind === "about" && (<>
            <div className="rb-row">
              <label className="rb-label" htmlFor={`pf-label-${profileId}`}>Name</label>
              <div className="rb-ctl">
                <input className="input" id={`pf-label-${profileId}`} style={{ width: "100%" }}
                       value={body.label ?? ""} readOnly={readOnly}
                       onChange={(e) => edit({ label: e.target.value })} />
              </div>
            </div>
            <div className="rb-row" style={{ borderBottom: 0 }}>
              <label className="rb-label rb-label-block" htmlFor={`pf-brief-${profileId}`}>
                Brief</label>
              <div className="rb-ctl">
                <p className="rb-hint">Read by the model beside the criteria: what a submission in
                  this language is, and how to read it.</p>
                <textarea className="ta short prose" id={`pf-brief-${profileId}`} rows={3}
                          value={body.brief ?? ""} readOnly={readOnly}
                          onChange={(e) => edit({ brief: e.target.value })} />
              </div>
            </div>
          </>)}

          {at.kind === "craft" && (<>
            <div className="rb-row">
              <div className="rb-label rb-label-block">The base says</div>
              <div className="rb-ctl">
                <ul className="small muted">
                  {(here?.credit_for ?? []).map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
            </div>
            <div className="rb-row" style={{ borderBottom: 0 }}>
              <div className="rb-label rb-label-block">
                In {body.label || live.label}
                <span className="rb-count">{(craft[at.id] ?? []).length} lines</span>
              </div>
              <div className="rb-ctl">
                <p className="rb-hint">Added after the base's lines, never instead of them. An
                  assignment that turns this criterion off takes these with it.</p>
                <Lines value={strs(craft[at.id])} add="Add a line" readOnly={readOnly}
                       onChange={(v) => edit({ craft: { ...craft, [at.id]: v } })} />
              </div>
            </div>
          </>)}

          {at.kind === "files" && (<>
            <div className="rb-row">
              <div className="rb-label rb-label-block">
                Counted as theirs
                <span className="rb-count">{strs(body.authored).length} patterns</span>
              </div>
              <div className="rb-ctl">
                <p className="rb-hint">Globs, relative to the submission root. A file matching
                  none of these reads as <code>other</code> in the authorship report.</p>
                <Lines value={strs(body.authored)} add="Add a pattern" mono rows={1}
                       onChange={(v) => edit({ authored: v })} readOnly={readOnly} />
              </div>
            </div>
            <div className="rb-row" style={{ borderBottom: 0 }}>
              <div className="rb-label rb-label-block">
                Ignored
                <span className="rb-count">{strs(body.ignored).length} patterns</span>
              </div>
              <div className="rb-ctl">
                <Lines value={strs(body.ignored)} add="Add a pattern" mono rows={1}
                       onChange={(v) => edit({ ignored: v })} readOnly={readOnly} />
              </div>
            </div>
          </>)}

          {at.kind === "testing" && (<>
            <div className="rb-row">
              <div className="rb-label rb-label-block">
                What should be tested
                <span className="rb-count">{strs(body.testing?.what).length} lines</span>
              </div>
              <div className="rb-ctl">
                <p className="rb-hint">Added to the base's expectations. Whether tests are
                  <em> required</em> is each assignment's call, not the language's.</p>
                <Lines value={strs(body.testing?.what)} add="Add an expectation"
                       readOnly={readOnly}
                       onChange={(v) => edit({ testing: { ...body.testing, what: v } })} />
              </div>
            </div>
            <div className="rb-row" style={{ borderBottom: 0 }}>
              <div className="rb-label rb-label-block">
                What to write them with
                <span className="rb-count">{strs(body.testing?.tools).length} suggestions</span>
              </div>
              <div className="rb-ctl">
                <Lines value={strs(body.testing?.tools)} add="Add a tool" mono rows={1}
                       readOnly={readOnly}
                       onChange={(v) => edit({ testing: { ...body.testing, tools: v } })} />
              </div>
            </div>
          </>)}
        </section>
      </div>

      {readOnly
        ? <p className="small muted" style={{ marginTop: 12 }}>
            A language profile changes how craft reads for every submission in that language,
            in every cohort — so it is not a cohort permission, and this panel is read-only for
            everybody but whoever administers this console.
          </p>
        : <>
      <div className="rb-save">
        <span className="rb-save-m">
          {dirty ? <>Unsaved changes. <strong>Nothing is written</strong> until you check and
                     confirm.</>
                 : "No changes yet."}
        </span>
        <span className="row gap">
          {dirty && (
            <button className="btn sm ghost" disabled={busy}
                    onClick={() => { setBody(live); setPre(null); setMsg(""); }}>
              Discard my edits</button>)}
          <button className="btn sm" disabled={busy || !dirty} onClick={() => go(false)}>
            {busy ? "Checking…" : "Check what this reaches"}</button>
        </span>
      </div>
      {pre && (
        <div className="alert" role="status" style={{ marginTop: 12 }}>
          <span className="ico">⚠️</span>
          <div className="body">
            <div className="t">Nothing has been written yet</div>
            <div className="m">{pre.note}</div>
            <Field wide label="Why"
                   hint="Kept with the version, so a later reader knows what this changed. It
reaches no student and no model.">
              <textarea className="ta short prose" rows={2} value={note} maxLength={2000}
                        placeholder="optional — what this edit is for"
                        onChange={(e) => setNote(e.target.value)} />
            </Field>
            <div className="row gap" style={{ marginTop: 8 }}>
              <button className="btn sm primary" disabled={busy} onClick={() => go(true)}>
                Save version {(meta.data?.reach.current_version ?? 0) + 1}</button>
              <button className="btn sm ghost" disabled={busy}
                      onClick={() => setPre(null)}>Cancel</button>
            </div>
          </div>
        </div>)}
      {msg !== "" && (
        <div className={`alert${msg.startsWith("Refused") || msg.startsWith("Infrastructure")
                                ? " refused" : ""}`} role="status" style={{ marginTop: 12 }}>
          <span className="ico">{msg.startsWith("Saved") ? "✅" : "⛔"}</span>
          <div className="body"><div className="m">{msg}</div></div>
        </div>)}
      {meta.data && <RubricVersions rubricId={profileId} rows={meta.data.versions}
                                    fetchVersion={getAdminProfileVersion}
                                    countLabel="criteria with craft"
                                    onLoad={(b) => {
                                      setBody(b as ProfileBody); setPre(null); setMsg("");
                                    }} />}
      {meta.error && <ErrorBox err={meta.error} onRetry={meta.reload} />}
        </>}
    </div>
  );
}
